import types from '../mutation-types';
import ScheduledMessagesAPI from '../../api/scheduledMessages';

export const state = {
  records: [],
  history: [],
  uiFlags: {
    isFetching: false,
    isFetchingHistory: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
  meta: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
  },
};

export const getters = {
  getScheduledMessages: _state => _state.records,
  getScheduledMessagesHistory: _state => _state.history,
  getUIFlags: _state => _state.uiFlags,
  getMeta: _state => _state.meta,

  getScheduledMessagesByContact: _state => contactId => {
    return _state.records.filter(
      message => message.contact_id === parseInt(contactId, 10)
    );
  },

  getScheduledMessagesByConversation: _state => conversationId => {
    return _state.records.filter(
      message => message.conversation_id === parseInt(conversationId, 10)
    );
  },

  getHistoryByContact: _state => contactId => {
    return _state.history.filter(
      message => message.contact_id === parseInt(contactId, 10)
    );
  },

  getUpcomingMessages: _state => {
    const now = new Date();
    return _state.records
      .filter(message => new Date(message.scheduled_at) > now)
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  },
};

export const actions = {
  async fetch({ commit }, params = {}) {
    commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isFetching: true });
    try {
      const { data } = await ScheduledMessagesAPI.get(params);
      commit(types.SET_SCHEDULED_MESSAGES, data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isFetching: false });
    }
  },

  async fetchUpcoming({ commit }, params = {}) {
    commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isFetching: true });
    try {
      const { data } = await ScheduledMessagesAPI.getUpcoming(params);
      commit(types.SET_SCHEDULED_MESSAGES, data.data || []);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isFetching: false });
    }
  },

  async fetchHistory({ commit }, params = {}) {
    commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isFetchingHistory: true });
    try {
      const { data } = await ScheduledMessagesAPI.getHistory(params);
      commit(types.SET_SCHEDULED_MESSAGES_HISTORY, data.data || []);
      if (data.meta) {
        commit(types.SET_SCHEDULED_MESSAGES_META, data.meta);
      }
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, {
        isFetchingHistory: false,
      });
    }
  },

  async fetchByContact({ commit }, { contactId, ...params }) {
    commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isFetching: true });
    try {
      const { data } = await ScheduledMessagesAPI.getByContact(
        contactId,
        params
      );
      commit(types.SET_SCHEDULED_MESSAGES, data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isFetching: false });
    }
  },

  async fetchByConversation({ commit }, { conversationId, ...params }) {
    commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isFetching: true });
    try {
      const { data } = await ScheduledMessagesAPI.getByConversation(
        conversationId,
        params
      );
      commit(types.SET_SCHEDULED_MESSAGES, data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isFetching: false });
    }
  },

  async create({ commit }, scheduledMessage) {
    commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isCreating: true });
    try {
      const { data } = await ScheduledMessagesAPI.create(scheduledMessage);
      commit(types.ADD_SCHEDULED_MESSAGE, data);
      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isCreating: false });
    }
  },

  async update({ commit }, { id, ...updateData }) {
    commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await ScheduledMessagesAPI.update(id, updateData);
      commit(types.UPDATE_SCHEDULED_MESSAGE, data);
      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isUpdating: false });
    }
  },

  async delete({ commit }, id) {
    commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isDeleting: true });
    try {
      await ScheduledMessagesAPI.delete(id);
      commit(types.DELETE_SCHEDULED_MESSAGE, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isDeleting: false });
    }
  },

  // Action to handle WebSocket updates
  updateFromSocket({ commit }, scheduledMessage) {
    commit(types.UPDATE_SCHEDULED_MESSAGE, scheduledMessage);
  },
};

export const mutations = {
  [types.SET_SCHEDULED_MESSAGES_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_SCHEDULED_MESSAGES](_state, data) {
    _state.records = Array.isArray(data) ? data : [];
  },

  [types.SET_SCHEDULED_MESSAGES_HISTORY](_state, data) {
    _state.history = Array.isArray(data) ? data : [];
  },

  [types.ADD_SCHEDULED_MESSAGE](_state, data) {
    _state.records = [..._state.records, data];
  },

  [types.UPDATE_SCHEDULED_MESSAGE](_state, data) {
    const index = _state.records.findIndex(record => record.id === data.id);
    if (index !== -1) {
      _state.records = [
        ..._state.records.slice(0, index),
        data,
        ..._state.records.slice(index + 1),
      ];
    }

    // Also update in history if exists
    const historyIndex = _state.history.findIndex(
      record => record.id === data.id
    );
    if (historyIndex !== -1) {
      _state.history = [
        ..._state.history.slice(0, historyIndex),
        data,
        ..._state.history.slice(historyIndex + 1),
      ];
    }
  },

  [types.DELETE_SCHEDULED_MESSAGE](_state, id) {
    _state.records = _state.records.filter(record => record.id !== id);
  },

  [types.SET_SCHEDULED_MESSAGES_META](_state, meta) {
    _state.meta = {
      currentPage: meta.current_page || 1,
      totalPages: meta.total_pages || 1,
      totalCount: meta.total_count || 0,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
